"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CommandExecutor = void 0;
/* --------------------------------------------------------------------------------------------
 *  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 * ------------------------------------------------------------------------------------------ */
const proc = require("child_process");
const fs = require("fs");
const os = require("os");
const util_1 = require("util");
const open = require("open");
const path = require("path");
const sudo_prompt_1 = require("@vscode/sudo-prompt");
const EventStreamEvents_1 = require("../EventStream/EventStreamEvents");
const InstallIdUtilities_1 = require("./InstallIdUtilities");
const CacheTimeConstants_1 = require("../Acquisition/CacheTimeConstants");
const StringConstants_1 = require("../Acquisition/StringConstants");
const LocalMemoryCacheSingleton_1 = require("../LocalMemoryCacheSingleton");
const FileUtilities_1 = require("./FileUtilities");
const ICommandExecutor_1 = require("./ICommandExecutor");
const LockUsedByThisInstanceSingleton_1 = require("./LockUsedByThisInstanceSingleton");
const TypescriptUtilities_1 = require("./TypescriptUtilities");
class CommandExecutor extends ICommandExecutor_1.ICommandExecutor {
    constructor(context, utilContext, validSudoCommands) {
        super(context, utilContext);
        this.validSudoCommands = validSudoCommands;
        this.pathTroubleshootingOption = 'Troubleshoot';
        this.englishOutputEnvironmentVariables = {
            LC_ALL: 'en_US.UTF-8',
            LANG: 'en_US.UTF-8',
            LANGUAGE: 'en',
            DOTNET_CLI_UI_LANGUAGE: 'en-US',
        }; // Not all systems have english installed -- not sure if it's safe to use this.
        this.sudoProcessScript = path.join(__dirname, 'install scripts', 'interprocess-communicator.sh');
        this.sudoProcessCommunicationDir = path.join(__dirname, LockUsedByThisInstanceSingleton_1.LockUsedByThisInstanceSingleton.SUDO_SESSION_ID);
        this.fileUtil = new FileUtilities_1.FileUtilities();
    }
    /**
     *
     * @returns The output of the command.
     */
    ExecSudoAsync(command_1) {
        return __awaiter(this, arguments, void 0, function* (command, terminalFailure = true) {
            var _a, _b, _c, _d, _e;
            const fullCommandString = CommandExecutor.prettifyCommandExecutorCommand(command, false);
            (_a = this.context) === null || _a === void 0 ? void 0 : _a.eventStream.post(new EventStreamEvents_1.CommandExecutionUnderSudoEvent(`The command ${fullCommandString} is being ran under sudo.`));
            const shellScript = this.sudoProcessScript;
            try {
                yield fs.promises.mkdir(this.sudoProcessCommunicationDir, { recursive: true });
            }
            catch (error) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                error.message = `${error === null || error === void 0 ? void 0 : error.message}\nFailed to create ${this.sudoProcessCommunicationDir}. Please check your permissions or install dotnet manually.`;
                (_b = this.context) === null || _b === void 0 ? void 0 : _b.eventStream.post(new EventStreamEvents_1.SudoDirCreationFailed(`The command ${fullCommandString} failed, as no directory could be made: ${JSON.stringify(error)}`));
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                if ((error === null || error === void 0 ? void 0 : error.code) !== 'EEXIST') {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    if ((error === null || error === void 0 ? void 0 : error.code) === 'EPERM' || (error === null || error === void 0 ? void 0 : error.code) === 'EACCES') {
                        this.sudoProcessCommunicationDir = path.dirname(this.sudoProcessScript);
                    }
                    else {
                        throw error;
                    }
                }
            }
            if (yield (0, TypescriptUtilities_1.isRunningUnderWSL)(this.context, this.utilityContext, this)) {
                // For WSL, vscode/sudo-prompt does not work.
                // This is because it relies on pkexec or a GUI app to popup and request sudo privilege.
                // GUI in WSL is not supported, so it will fail.
                // We had a working implementation that opens a vscode box and gets the user password, but that will require more security analysis.
                const err = new EventStreamEvents_1.DotnetWSLSecurityError(new EventStreamEvents_1.EventCancellationError('DotnetWSLSecurityError', `Automatic .NET SDK Installation is not yet supported in WSL due to VS Code & WSL limitations.
Please install the .NET SDK manually by following https://learn.microsoft.com/en-us/dotnet/core/install/linux-ubuntu. Then, add it to the path by following https://github.com/dotnet/vscode-dotnet-runtime/blob/main/Documentation/troubleshooting-runtime.md#manually-installing-net`), (0, InstallIdUtilities_1.getInstallFromContext)(this.context));
                (_c = this.context) === null || _c === void 0 ? void 0 : _c.eventStream.post(err);
                throw err.error;
            }
            const waitForLockTimeMs = ((_d = this.context) === null || _d === void 0 ? void 0 : _d.timeoutSeconds) ? (((_e = this.context) === null || _e === void 0 ? void 0 : _e.timeoutSeconds) * 1000 / 3) : 180000;
            // @ts-expect-error We want to hold the lock and sometimes return a bool, sometimes a CommandExecutorResult. The bool will never be returned if runCommand is true, so this makes the compiler accept this (its bad ik).
            return (0, TypescriptUtilities_1.executeWithLock)(this.context.eventStream, false, (0, StringConstants_1.RUN_UNDER_SUDO_LOCK)(this.sudoProcessScript), CacheTimeConstants_1.SUDO_LOCK_PING_DURATION_MS, waitForLockTimeMs, () => __awaiter(this, void 0, void 0, function* () {
                this.startupSudoProc(fullCommandString, shellScript, terminalFailure).catch(() => { });
                return this.sudoProcIsLive(terminalFailure, fullCommandString, undefined, true);
            }));
        });
    }
    /**
     *
     * @param fullCommandString the command that will be run by the master process once it is spawned, not super relevant here, used for logging.
     * @param shellScriptPath the path of the shell script file for the process to run that should loop and follow the protocol procedure
     * @param terminalFailure whether if we cannot start the sudo process, should we fail the entire program.
     * @returns The string result of either trying to spawn the sudo master process, or the status code of that attempt depending on the return mode.
     */
    startupSudoProc(fullCommandString, shellScriptPath, terminalFailure) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (LockUsedByThisInstanceSingleton_1.LockUsedByThisInstanceSingleton.getInstance().hasSpawnedSudoSuccessfullyWithoutDeath()) {
                if (yield this.sudoProcIsLive(false, fullCommandString)) {
                    return '0';
                }
            }
            // Launch the process under sudo
            (_a = this.context) === null || _a === void 0 ? void 0 : _a.eventStream.post(new EventStreamEvents_1.CommandExecutionUserAskDialogueEvent(`Prompting user for command ${fullCommandString} under sudo.`));
            const options = { name: this.getSanitizedCallerName() };
            fs.chmodSync(shellScriptPath, 0o500);
            const timeoutSeconds = Math.max(100, this.context.timeoutSeconds);
            return new Promise((resolve, reject) => {
                var _a;
                const timeout = setTimeout(() => {
                    var _a;
                    const timeOutEvent = new EventStreamEvents_1.CommandExecutionUserCompletedDialogueEvent(`The process spawn: ${fullCommandString} failed to run under sudo.`);
                    (_a = this.context) === null || _a === void 0 ? void 0 : _a.eventStream.post(timeOutEvent);
                    const finalTimeoutErr = new Error(timeOutEvent.eventMessage);
                    LockUsedByThisInstanceSingleton_1.LockUsedByThisInstanceSingleton.getInstance().setSudoProcError(finalTimeoutErr);
                    return reject(finalTimeoutErr);
                }, timeoutSeconds * 1000);
                (0, sudo_prompt_1.exec)((`"${shellScriptPath}" "${this.sudoProcessCommunicationDir}" "${timeoutSeconds}" ${(_a = this.validSudoCommands) === null || _a === void 0 ? void 0 : _a.join(' ')} &`), options, (error, stdout, stderr) => {
                    var _a, _b, _c, _d;
                    (_a = this.context) === null || _a === void 0 ? void 0 : _a.eventStream.post(new EventStreamEvents_1.CommandExecutionStdOut(`The process spawn: ${fullCommandString} encountered stdout, continuing
${stdout}`));
                    (_b = this.context) === null || _b === void 0 ? void 0 : _b.eventStream.post(new EventStreamEvents_1.CommandExecutionStdError(`The process spawn: ${fullCommandString} encountered stderr, continuing
${stderr}`));
                    if (error !== null && error !== undefined) {
                        (_c = this.context) === null || _c === void 0 ? void 0 : _c.eventStream.post(new EventStreamEvents_1.CommandExecutionUserCompletedDialogueEvent(`The process spawn: ${fullCommandString} failed to run under sudo.`));
                        clearTimeout(timeout);
                        LockUsedByThisInstanceSingleton_1.LockUsedByThisInstanceSingleton.getInstance().setSudoProcError(error);
                        return reject(error);
                    }
                    (_d = this.context) === null || _d === void 0 ? void 0 : _d.eventStream.post(new EventStreamEvents_1.CommandExecutionUserCompletedDialogueEvent(`The process spawn: ${fullCommandString} successfully ran under sudo.`));
                    clearTimeout(timeout);
                    return resolve('0');
                });
            });
        });
    }
    /**
     *
     * @param errorIfDead set this to true if we should terminally fail if the master process is not yet alive
     * @returns a boolean, true if the master process is live, false otherwise. If command mode is used, returns the exit code of the command holding the lock after checking live state.
     * @remarks only call if already holding the sudo lock.
     */
    sudoProcIsLive(errorIfDead_1, fullCommandString_1, maxTimeoutTimeMs_1) {
        return __awaiter(this, arguments, void 0, function* (errorIfDead, fullCommandString, maxTimeoutTimeMs, runCommand = false) {
            var _a, _b, _c, _d, _e, _f;
            const processAliveOkSentinelFile = path.join(this.sudoProcessCommunicationDir, 'ok.txt');
            const waitForLockTimeMs = maxTimeoutTimeMs ? maxTimeoutTimeMs : (((_a = this.context) === null || _a === void 0 ? void 0 : _a.timeoutSeconds) !== undefined ? (Math.max(this.context.timeoutSeconds * 1000 / 5, 100)) : 180000);
            const waitForSudoResponseTimeMs = waitForLockTimeMs * 0.75; // Arbitrary, but this should be less than the time to get the lock.
            yield this.fileUtil.wipeDirectory(this.sudoProcessCommunicationDir, (_b = this.context) === null || _b === void 0 ? void 0 : _b.eventStream, ['.txt']);
            yield this.fileUtil.writeFileOntoDisk('', processAliveOkSentinelFile, (_c = this.context) === null || _c === void 0 ? void 0 : _c.eventStream);
            (_d = this.context) === null || _d === void 0 ? void 0 : _d.eventStream.post(new EventStreamEvents_1.SudoProcAliveCheckBegin(`Looking for Sudo Process Master, wrote OK file. ${new Date().toISOString()}`));
            yield (0, TypescriptUtilities_1.loopWithTimeoutOnCond)(100, waitForSudoResponseTimeMs, function processRespondedByDeletingOkFile() {
                if (LockUsedByThisInstanceSingleton_1.LockUsedByThisInstanceSingleton.getInstance().sudoProcError() !== null) {
                    return true;
                }
                return !(fs.existsSync(processAliveOkSentinelFile));
            }, function setProcessIsAlive() {
                if (LockUsedByThisInstanceSingleton_1.LockUsedByThisInstanceSingleton.getInstance().sudoProcError() === null) {
                    LockUsedByThisInstanceSingleton_1.LockUsedByThisInstanceSingleton.getInstance().setCurrentSudoCheckAsAlive(true);
                }
            }, this.context.eventStream, new EventStreamEvents_1.SudoProcCommandExchangePing(`Ping : Waiting. ${new Date().toISOString()}`))
                .catch(error => {
                // Let the rejected promise get handled below. This is required to not make an error from the checking if this promise is alive
            });
            const isLive = LockUsedByThisInstanceSingleton_1.LockUsedByThisInstanceSingleton.getInstance().isCurrentSudoProcCheckAlive();
            (_e = this.context) === null || _e === void 0 ? void 0 : _e.eventStream.post(new EventStreamEvents_1.SudoProcAliveCheckEnd(`Finished Sudo Process Master: Is Alive? ${isLive}. ${new Date().toISOString()}
                    waitForLockTimeMs: ${waitForLockTimeMs} with lockTime ${waitForLockTimeMs} and responseTime ${waitForSudoResponseTimeMs}`));
            // The sudo process spawned by vscode does not exit unless it fails or times out after an hour. We can't await it as we need it to persist.
            // If someone cancels the install, we store that error here since this gets awaited to prevent further code statement control flow from executing.
            const errThrownBySudoLib = LockUsedByThisInstanceSingleton_1.LockUsedByThisInstanceSingleton.getInstance().sudoProcError();
            if (errThrownBySudoLib !== null) {
                LockUsedByThisInstanceSingleton_1.LockUsedByThisInstanceSingleton.getInstance().setSudoProcError(null); // if someone rejects pw prompt once, we do not want to be in an err state forever.
                const parsedErr = this.parseVSCodeSudoExecError(errThrownBySudoLib, fullCommandString);
                throw parsedErr;
            }
            if (!LockUsedByThisInstanceSingleton_1.LockUsedByThisInstanceSingleton.getInstance().isCurrentSudoProcCheckAlive() && errorIfDead) {
                const err = new EventStreamEvents_1.TimeoutSudoProcessSpawnerError(new EventStreamEvents_1.EventCancellationError('TimeoutSudoProcessSpawnerError', `We are unable to spawn the process to run commands under sudo for installing .NET.
            Process Directory: ${this.sudoProcessCommunicationDir} failed with error mode: ${errorIfDead}.
            It had previously spawned: ${LockUsedByThisInstanceSingleton_1.LockUsedByThisInstanceSingleton.getInstance().hasSpawnedSudoSuccessfullyWithoutDeath()}.`), (0, InstallIdUtilities_1.getInstallFromContext)(this.context));
                (_f = this.context) === null || _f === void 0 ? void 0 : _f.eventStream.post(err);
                throw err.error;
            }
            LockUsedByThisInstanceSingleton_1.LockUsedByThisInstanceSingleton.getInstance().setCurrentSudoCheckAsAlive(false);
            if (!runCommand) {
                return isLive;
            }
            else {
                // Hold the lock during the is alive check so nobody kills it in between
                return this.executeSudoViaProcessCommunication(fullCommandString, errorIfDead, true);
            }
        });
    }
    /**
     *
     * @param commandToExecuteString The command to tell the sudo'd master process to execute. It must be live.
     * @param terminalFailure Whether to fail if we never get a response from the sudo process.
     * @param failOnNonZeroExit Whether to fail if we get an exit code from the command besides 0.
     * @returns The output string of the command, or the string status code, depending on the mode of execution.
     */
    executeSudoViaProcessCommunication(commandToExecuteString_1, terminalFailure_1) {
        return __awaiter(this, arguments, void 0, function* (commandToExecuteString, terminalFailure, holdingLock = false) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
            let commandOutputJson = null;
            const noStatusCodeErrorCode = '1220'; // Special failure code for if code is never set error
            const commandFile = path.join(this.sudoProcessCommunicationDir, 'command.txt');
            const stderrFile = path.join(this.sudoProcessCommunicationDir, 'stderr.txt');
            const stdoutFile = path.join(this.sudoProcessCommunicationDir, 'stdout.txt');
            const statusFile = path.join(this.sudoProcessCommunicationDir, 'status.txt');
            const outputFile = path.join(this.sudoProcessCommunicationDir, 'output.txt');
            yield this.fileUtil.wipeDirectory(this.sudoProcessCommunicationDir, (_a = this.context) === null || _a === void 0 ? void 0 : _a.eventStream, ['.txt', '.json']);
            yield this.fileUtil.writeFileOntoDisk(`${commandToExecuteString}`, commandFile, (_b = this.context) === null || _b === void 0 ? void 0 : _b.eventStream);
            (_c = this.context) === null || _c === void 0 ? void 0 : _c.eventStream.post(new EventStreamEvents_1.SudoProcCommandExchangeBegin(`Handing command off to master process. ${new Date().toISOString()}`));
            (_d = this.context) === null || _d === void 0 ? void 0 : _d.eventStream.post(new EventStreamEvents_1.CommandProcessorExecutionBegin(`The command ${commandToExecuteString} was forwarded to the master process to run.`));
            const commandStartTime = process.hrtime.bigint();
            const waitTimeMs = ((_e = this.context) === null || _e === void 0 ? void 0 : _e.timeoutSeconds) ? (Math.max(((_f = this.context) === null || _f === void 0 ? void 0 : _f.timeoutSeconds) * 1000, 1000)) : 600000;
            const sampleRateMs = 100;
            yield (0, TypescriptUtilities_1.loopWithTimeoutOnCond)(sampleRateMs, waitTimeMs, function ProcessFinishedExecutingAndWroteOutput() { return fs.existsSync(outputFile); }, function doNothing() { ; }, this.context.eventStream, new EventStreamEvents_1.SudoProcCommandExchangePing(`Ping : Waiting, at rate ${sampleRateMs} with timeout ${waitTimeMs} ${new Date().toISOString()}`))
                .catch(error => {
                var _a;
                (_a = this.context) === null || _a === void 0 ? void 0 : _a.eventStream.post(new EventStreamEvents_1.FailedToRunSudoCommand(`The command ${commandToExecuteString} failed to run: ${JSON.stringify(error !== null && error !== void 0 ? error : '')}.`));
                // Let the rejected promise get handled below. This is required to not make an error from the checking if this promise is alive
            });
            commandOutputJson = {
                stdout: (yield this.fileUtil.read(stdoutFile)).trim(),
                stderr: (yield this.fileUtil.read(stderrFile)).trim(),
                status: (yield this.fileUtil.read(statusFile)).trim()
            };
            (_g = this.context) === null || _g === void 0 ? void 0 : _g.eventStream.post(new EventStreamEvents_1.SudoProcCommandExchangeEnd(`Finished or timed out with master process. ${new Date().toISOString()}`));
            if (!commandOutputJson && terminalFailure) {
                const err = new EventStreamEvents_1.TimeoutSudoCommandExecutionError(new EventStreamEvents_1.EventCancellationError('TimeoutSudoCommandExecutionError', `Timeout: The master process with command ${commandToExecuteString} never finished executing.
        Process Directory: ${this.sudoProcessCommunicationDir} failed with error mode: ${terminalFailure}.
        It had previously spawned: ${LockUsedByThisInstanceSingleton_1.LockUsedByThisInstanceSingleton.getInstance().hasSpawnedSudoSuccessfullyWithoutDeath()}.`), (0, InstallIdUtilities_1.getInstallFromContext)(this.context));
                (_h = this.context) === null || _h === void 0 ? void 0 : _h.eventStream.post(err);
                throw err.error;
            }
            else if (!commandOutputJson) {
                (_j = this.context) === null || _j === void 0 ? void 0 : _j.eventStream.post(new EventStreamEvents_1.CommandProcessesExecutionFailureNonTerminal(`The command ${commandToExecuteString} never finished under the process, but it was marked non terminal.`));
            }
            else {
                (_k = this.context) === null || _k === void 0 ? void 0 : _k.eventStream.post(new EventStreamEvents_1.CommandProcessorExecutionEnd(`The command ${commandToExecuteString} was finished by the master process, as ${outputFile} was found.`));
                this.logCommandResult(commandOutputJson, commandToExecuteString, commandStartTime, (_m = (_l = commandToExecuteString.split(' ')) === null || _l === void 0 ? void 0 : _l[0]) !== null && _m !== void 0 ? _m : 'sudo');
                if (commandOutputJson.status !== '0' && terminalFailure) {
                    const err = new EventStreamEvents_1.CommandExecutionNonZeroExitFailure(new EventStreamEvents_1.EventBasedError('CommandExecutionNonZeroExitFailure', `Cancelling .NET Install, as command ${commandToExecuteString} returned with status ${commandOutputJson.status}.
        ${commandOutputJson.stderr}.`), (0, InstallIdUtilities_1.getInstallFromContext)(this.context));
                    (_o = this.context) === null || _o === void 0 ? void 0 : _o.eventStream.post(err);
                    throw err.error;
                }
            }
            yield this.fileUtil.wipeDirectory(this.sudoProcessCommunicationDir, (_p = this.context) === null || _p === void 0 ? void 0 : _p.eventStream, ['.txt']);
            return commandOutputJson !== null && commandOutputJson !== void 0 ? commandOutputJson : { stdout: '', stderr: '', status: noStatusCodeErrorCode };
        });
    }
    /**
     * @returns 0 if the sudo master process was ended, 1 if it was not.
     * @remarks holds the sudo lock
     */
    endSudoProcessMaster(eventStream) {
        return __awaiter(this, void 0, void 0, function* () {
            if (os.platform() !== 'linux' || LockUsedByThisInstanceSingleton_1.LockUsedByThisInstanceSingleton.getInstance().hasSpawnedSudoSuccessfullyWithoutDeath() === false) {
                return 0;
            }
            yield (0, TypescriptUtilities_1.executeWithLock)(this.context.eventStream, false, (0, StringConstants_1.RUN_UNDER_SUDO_LOCK)(this.sudoProcessCommunicationDir), CacheTimeConstants_1.SUDO_LOCK_PING_DURATION_MS, this.context.timeoutSeconds * 1000 / 5, () => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c, _d;
                yield this.fileUtil.wipeDirectory(this.sudoProcessCommunicationDir, (_a = this.context) === null || _a === void 0 ? void 0 : _a.eventStream, ['.txt']);
                const processExitFile = path.join(this.sudoProcessCommunicationDir, 'exit.txt');
                yield this.fileUtil.writeFileOntoDisk('', processExitFile, (_b = this.context) === null || _b === void 0 ? void 0 : _b.eventStream);
                const waitTimeMs = ((_c = this.context) === null || _c === void 0 ? void 0 : _c.timeoutSeconds) ? (((_d = this.context) === null || _d === void 0 ? void 0 : _d.timeoutSeconds) * 1000 / 5) : 600000;
                try {
                    yield (0, TypescriptUtilities_1.loopWithTimeoutOnCond)(100, waitTimeMs, function processRespondedByDeletingExitFile() { return !fs.existsSync(processExitFile); }, function returnZeroOnExit() { LockUsedByThisInstanceSingleton_1.LockUsedByThisInstanceSingleton.getInstance().killingSudoProc(); }, this.context.eventStream, new EventStreamEvents_1.SudoProcCommandExchangePing(`Ping : Waiting to exit sudo process master. ${new Date().toISOString()}`));
                }
                catch (error) {
                    eventStream.post(new EventStreamEvents_1.TriedToExitMasterSudoProcess(`Tried to exit sudo master process: FAILED. ${error ? JSON.stringify(error) : ''}`));
                }
                eventStream.post(new EventStreamEvents_1.TriedToExitMasterSudoProcess(`Tried to exit sudo master process: exit code ${LockUsedByThisInstanceSingleton_1.LockUsedByThisInstanceSingleton.getInstance().hasSpawnedSudoSuccessfullyWithoutDeath()}`));
            }));
            try {
                fs.rmdirSync(this.sudoProcessCommunicationDir, { recursive: true });
            }
            catch (error) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                if ((error === null || error === void 0 ? void 0 : error.code) !== 'ENOENT') {
                    eventStream.post(new EventStreamEvents_1.SudoDirCreationFailed(`The command ${this.sudoProcessCommunicationDir} failed to rm the sudo directory: ${JSON.stringify(error)}`));
                }
            }
            return LockUsedByThisInstanceSingleton_1.LockUsedByThisInstanceSingleton.getInstance().hasSpawnedSudoSuccessfullyWithoutDeath() ? 1 : 0;
        });
    }
    executeMultipleCommands(commands_1, options_1) {
        return __awaiter(this, arguments, void 0, function* (commands, options, terminalFailure = true) {
            const results = [];
            for (const command of commands) {
                results.push(yield this.execute(command, options, terminalFailure));
            }
            return results;
        });
    }
    /**
     *
     * @param workingDirectory The directory to execute in. Only works for non sudo commands.
     * @param terminalFailure Whether to throw up an error when executing under sudo or suppress it and return stderr
     * @param options the dictionary of options to forward to the child_process. Set dotnetInstallToolCacheTtlMs=number to cache the result with a ttl and also use cached results.
     * @returns the result(s) of each command. Can throw generically if the command fails.
     */
    execute(command_1) {
        return __awaiter(this, arguments, void 0, function* (command, options = null, terminalFailure = true) {
            var _a, _b, _c, _d, _e, _f;
            var _g, _h;
            const fullCommandString = `${command.commandRoot} ${command.commandParts.join(' ')}`;
            let useCache = false;
            // Remove this when https://github.com/typescript-eslint/typescript-eslint/issues/2728 is done
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (options) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                (_a = options.cwd) !== null && _a !== void 0 ? _a : (options.cwd = path.resolve(__dirname));
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                (_b = options.shell) !== null && _b !== void 0 ? _b : (options.shell = true);
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                options.encoding = 'utf8';
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                (_c = options.env) !== null && _c !== void 0 ? _c : (options.env = Object.assign({}, process.env));
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                (_d = (_g = options.env).DOTNET_CLI_UI_LANGUAGE) !== null && _d !== void 0 ? _d : (_g.DOTNET_CLI_UI_LANGUAGE = 'en-US');
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                (_e = (_h = options.env).DOTNET_NOLOGO) !== null && _e !== void 0 ? _e : (_h.DOTNET_NOLOGO = 'true');
            }
            else {
                options = {
                    cwd: path.resolve(__dirname), shell: true, encoding: 'utf8', env: Object.assign(Object.assign({}, process.env), { DOTNET_CLI_UI_LANGUAGE: 'en-US', DOTNET_NOLOGO: 'true' })
                };
            }
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (options === null || options === void 0 ? void 0 : options.dotnetInstallToolCacheTtlMs) {
                useCache = true;
                const cachedResult = LocalMemoryCacheSingleton_1.LocalMemoryCacheSingleton.getInstance().getCommand({ command, options }, this.context);
                if (cachedResult !== undefined) {
                    return cachedResult;
                }
            }
            if (command.runUnderSudo && os.platform() === 'linux') {
                const sudoResult = yield this.ExecSudoAsync(command, terminalFailure);
                if (useCache) {
                    LocalMemoryCacheSingleton_1.LocalMemoryCacheSingleton.getInstance().putCommand({ command, options }, sudoResult, this.context);
                }
                return sudoResult;
            }
            else {
                const { env } = options, optionsWithoutEnv = __rest(options, ["env"]);
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                (_f = this.context) === null || _f === void 0 ? void 0 : _f.eventStream.post(new EventStreamEvents_1.CommandExecutionEvent(`Executing command ${fullCommandString} with options ${JSON.stringify((options === null || options === void 0 ? void 0 : options.env) !== null && (options === null || options === void 0 ? void 0 : options.env) !== undefined ? Object.assign({ env: (0, TypescriptUtilities_1.minimizeEnvironment)(env) }, optionsWithoutEnv) : options)}.`));
                if (command.runUnderSudo) {
                    // Remove this when https://github.com/typescript-eslint/typescript-eslint/issues/2728 is done
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    options.name = this.getSanitizedCallerName();
                    return new Promise((resolve, reject) => {
                        (0, sudo_prompt_1.exec)(fullCommandString, options, (error, execStdout, execStderr) => {
                            var _a, _b;
                            if (error && terminalFailure && !((_a = error === null || error === void 0 ? void 0 : error.message) === null || _a === void 0 ? void 0 : _a.includes('screen size is bogus'))) {
                                return reject(this.parseVSCodeSudoExecError(error, fullCommandString));
                            }
                            else if (error) {
                                (_b = this.context) === null || _b === void 0 ? void 0 : _b.eventStream.post(new EventStreamEvents_1.CommandExecutionStdError(`The command ${fullCommandString} encountered ERROR: ${JSON.stringify(error)}`));
                            }
                            const result = { status: error ? error.message : '0', stderr: execStderr, stdout: execStdout };
                            if (useCache) {
                                LocalMemoryCacheSingleton_1.LocalMemoryCacheSingleton.getInstance().putCommand({ command, options }, result, this.context);
                            }
                            return resolve(result);
                        });
                    });
                }
                const commandStartTime = process.hrtime.bigint();
                const commandResult = yield (0, util_1.promisify)(proc.exec)(fullCommandString, options).then(fulfilled => {
                    var _a, _b, _c, _d;
                    // If any status besides 0 is returned, an error is thrown by nodejs
                    return { stdout: (_b = (_a = fulfilled.stdout) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : '', stderr: (_d = (_c = fulfilled.stderr) === null || _c === void 0 ? void 0 : _c.toString()) !== null && _d !== void 0 ? _d : '', status: '0' };
                }, rejected => // Rejected object: error type with stderr : Buffer, stdout : Buffer ... with .code (number) or .signal (string)}
                 {
                    var _a, _b, _c, _d, _e, _f, _g, _h;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    const result = { stdout: (_b = (_a = rejected === null || rejected === void 0 ? void 0 : rejected.stdout) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : '', stderr: (_d = (_c = rejected === null || rejected === void 0 ? void 0 : rejected.stderr) === null || _c === void 0 ? void 0 : _c.toString()) !== null && _d !== void 0 ? _d : '', status: (_h = (_f = (_e = rejected === null || rejected === void 0 ? void 0 : rejected.code) === null || _e === void 0 ? void 0 : _e.toString()) !== null && _f !== void 0 ? _f : (_g = rejected === null || rejected === void 0 ? void 0 : rejected.signal) === null || _g === void 0 ? void 0 : _g.toString()) !== null && _h !== void 0 ? _h : '' };
                    if (terminalFailure) {
                        this.logCommandResult(result, fullCommandString, commandStartTime, command.commandRoot);
                        throw rejected !== null && rejected !== void 0 ? rejected : new Error(`Spawning ${fullCommandString} failed with an unspecified error.`); // according to nodejs spec, this should never be possible
                    }
                    else {
                        // signal is a string or obj, code is a number
                        return result;
                    }
                });
                this.logCommandResult(commandResult, fullCommandString, commandStartTime, command.commandRoot);
                if (useCache) {
                    LocalMemoryCacheSingleton_1.LocalMemoryCacheSingleton.getInstance().putCommand({ command, options }, commandResult, this.context);
                }
                return commandResult;
            }
        });
    }
    logCommandResult(commandResult, fullCommandStringForTelemetryOnly, commandStartTime, commandRoot) {
        var _a, _b, _c, _d;
        const durationMs = (Number(process.hrtime.bigint() - commandStartTime) / 1000000).toFixed(2);
        (_a = this.context) === null || _a === void 0 ? void 0 : _a.eventStream.post(new EventStreamEvents_1.CommandExecutionTimer(`The command ${fullCommandStringForTelemetryOnly} took ${durationMs} ms to run.`, durationMs, commandRoot, fullCommandStringForTelemetryOnly));
        (_b = this.context) === null || _b === void 0 ? void 0 : _b.eventStream.post(new EventStreamEvents_1.CommandExecutionStatusEvent(`The command ${fullCommandStringForTelemetryOnly} exited:
${commandResult.status}.`));
        (_c = this.context) === null || _c === void 0 ? void 0 : _c.eventStream.post(new EventStreamEvents_1.CommandExecutionStdOut(`The command ${fullCommandStringForTelemetryOnly} encountered stdout:
${commandResult.stdout}`));
        (_d = this.context) === null || _d === void 0 ? void 0 : _d.eventStream.post(new EventStreamEvents_1.CommandExecutionStdError(`The command ${fullCommandStringForTelemetryOnly} encountered stderr:
${commandResult.stderr}`));
    }
    parseVSCodeSudoExecError(error, fullCommandString) {
        // 'permission' comes from an unlocalized string: https://github.com/bpasero/sudo-prompt/blob/21d9308edcf970f0a9ee0580c539b1457b3dc45b/index.js#L678
        // if you reject on the password prompt on windows before SDK window pops up, no code will be set, so we need to check for this string.
        var _a, _b, _c;
        // Remove this when https://github.com/typescript-eslint/typescript-eslint/issues/2728 is done
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if ((error === null || error === void 0 ? void 0 : error.code) === 126 || ((_a = error === null || error === void 0 ? void 0 : error.message) === null || _a === void 0 ? void 0 : _a.includes('permission'))) {
            const cancelledErr = new EventStreamEvents_1.CommandExecutionUserRejectedPasswordRequest(new EventStreamEvents_1.EventCancellationError('CommandExecutionUserRejectedPasswordRequest', `Cancelling .NET Install, as command ${fullCommandString} failed.
The user refused the password prompt.`), (0, InstallIdUtilities_1.getInstallFromContext)(this.context));
            (_b = this.context) === null || _b === void 0 ? void 0 : _b.eventStream.post(cancelledErr);
            return cancelledErr.error;
        }
        // Remove this when https://github.com/typescript-eslint/typescript-eslint/issues/2728 is done
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        else if ((error === null || error === void 0 ? void 0 : error.code) === 111777) {
            const securityErr = new EventStreamEvents_1.CommandExecutionUnknownCommandExecutionAttempt(new EventStreamEvents_1.EventCancellationError('CommandExecutionUnknownCommandExecutionAttempt', `Cancelling .NET Install, as command ${fullCommandString} is UNKNOWN.
Please report this at https://github.com/dotnet/vscode-dotnet-runtime/issues.`), (0, InstallIdUtilities_1.getInstallFromContext)(this.context));
            (_c = this.context) === null || _c === void 0 ? void 0 : _c.eventStream.post(securityErr);
            return securityErr.error;
        }
        else {
            return error;
        }
    }
    /**
     *
     * @param commandRoots The first word of each command to try
     * @param matchingCommandParts Any follow up words in that command to execute, matching in the same order as commandRoots
     * @remarks You can pass a set of options per command which must match the index of each command.
     * @returns the index of the working command you provided, if no command works, -1.
     */
    tryFindWorkingCommand(commands, options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            let workingCommand = null;
            let optIdx = 0;
            for (const command of commands) {
                try {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    const cmdFoundOutput = (yield this.execute(command, (_a = options === null || options === void 0 ? void 0 : options[optIdx]) !== null && _a !== void 0 ? _a : options, false)).status;
                    if (cmdFoundOutput === '0') {
                        workingCommand = command;
                        (_b = this.context) === null || _b === void 0 ? void 0 : _b.eventStream.post(new EventStreamEvents_1.DotnetAlternativeCommandFoundEvent(`The command ${command.commandRoot} was found.`));
                        break;
                    }
                    else {
                        (_c = this.context) === null || _c === void 0 ? void 0 : _c.eventStream.post(new EventStreamEvents_1.DotnetCommandNotFoundEvent(`The command ${command.commandRoot} was NOT found, no error was thrown.`));
                    }
                }
                catch (err) {
                    // Do nothing. The error should be raised higher up.
                    (_d = this.context) === null || _d === void 0 ? void 0 : _d.eventStream.post(new EventStreamEvents_1.DotnetCommandNotFoundEvent(`The command ${command.commandRoot} was NOT found, and we caught any errors.`));
                }
                ++optIdx;
            }
            return workingCommand;
        });
    }
    setEnvironmentVariable(variable, value, vscodeContext, failureWarningMessage, nonWinFailureMessage) {
        return __awaiter(this, void 0, void 0, function* () {
            let environmentEditExitCode = 0;
            process.env[variable] = value;
            vscodeContext.setVSCodeEnvironmentVariable(variable, value);
            if (os.platform() === 'win32') {
                const setShellVariable = CommandExecutor.makeCommand(`set`, [`${variable}=${value}`]);
                const setSystemVariable = CommandExecutor.makeCommand(`setx`, [`${variable}`, `"${value}"`]);
                try {
                    const shellEditResponse = (yield this.execute(setShellVariable, null, false)).status;
                    environmentEditExitCode += Number(shellEditResponse[0]);
                    const systemEditResponse = (yield this.execute(setSystemVariable, null, false)).status;
                    environmentEditExitCode += Number(systemEditResponse[0]);
                }
                catch (error) {
                    environmentEditExitCode = 1;
                }
            }
            else {
                // export var=value does not do anything, because on osx and linux processes cannot edit above proc variables.
                // We could try to edit etc/environment on ubuntu, then .profile/.bash_rc/.zsh etc on osx, but we'd like to avoid being intrusive.
                failureWarningMessage = nonWinFailureMessage ? failureWarningMessage : nonWinFailureMessage;
                environmentEditExitCode = 1;
            }
            if (environmentEditExitCode !== 0 && failureWarningMessage) {
                this.utilityContext.ui.showWarningMessage(failureWarningMessage, () => { });
            }
        });
    }
    setPathEnvVar(pathAddition, troubleshootingUrl, displayWorker, vscodeContext, isGlobal) {
        if (!isGlobal || os.platform() === 'linux') {
            // Set user PATH variable. The .NET SDK Installer does this for us on Win/Mac.
            let pathCommand;
            if (os.platform() === 'win32') {
                pathCommand = this.getWindowsPathCommand(pathAddition);
            }
            else {
                pathCommand = this.getLinuxPathCommand(pathAddition);
            }
            if (pathCommand !== undefined) {
                this.runPathCommand(pathCommand, troubleshootingUrl, displayWorker);
            }
        }
        // Set PATH for VSCode terminal instances
        if (!process.env.PATH.includes(pathAddition)) {
            vscodeContext.appendToEnvironmentVariable('PATH', path.delimiter + pathAddition);
            process.env.PATH += path.delimiter + pathAddition;
        }
    }
    getSanitizedCallerName() {
        var _a, _b, _c;
        // The '.' character is not allowed for sudo-prompt so we use 'NET'
        let sanitizedCallerName = (_c = (_b = (_a = this.context) === null || _a === void 0 ? void 0 : _a.acquisitionContext) === null || _b === void 0 ? void 0 : _b.requestingExtensionId) === null || _c === void 0 ? void 0 : _c.replace(/[^0-9a-z]/gi, ''); // Remove non-alphanumerics per OS requirements
        sanitizedCallerName = sanitizedCallerName === null || sanitizedCallerName === void 0 ? void 0 : sanitizedCallerName.substring(0, 69); // 70 Characters is the maximum limit we can use for the prompt.
        return sanitizedCallerName !== null && sanitizedCallerName !== void 0 ? sanitizedCallerName : 'NET Install Tool';
    }
    getLinuxPathCommand(pathAddition) {
        const profileFile = os.platform() === 'darwin' ? path.join(os.homedir(), '.zshrc') : path.join(os.homedir(), '.profile');
        if (fs.existsSync(profileFile) && fs.readFileSync(profileFile).toString().includes(pathAddition)) {
            // No need to add to PATH again
            return undefined;
        }
        return `echo 'export PATH="${pathAddition}:$PATH"' >> ${profileFile}`;
    }
    getWindowsPathCommand(pathAddition) {
        if (process.env.PATH && process.env.PATH.includes(pathAddition)) {
            // No need to add to PATH again
            return undefined;
        }
        return `for /F "skip=2 tokens=1,2*" %A in ('%SystemRoot%\\System32\\reg.exe query "HKCU\\Environment" /v "Path" 2^>nul') do ` +
            `(%SystemRoot%\\System32\\reg.exe ADD "HKCU\\Environment" /v Path /t REG_SZ /f /d "${pathAddition};%C")`;
    }
    runPathCommand(pathCommand, troubleshootingUrl, displayWorker) {
        try {
            // this should be optimized eventually but its called only once and in mostly deprecated scenarios
            proc.execSync(pathCommand);
        }
        catch (error) {
            displayWorker.showWarningMessage(`Unable to add SDK to the PATH: ${JSON.stringify(error)}`, (response) => {
                if (response === this.pathTroubleshootingOption) {
                    open(`${troubleshootingUrl}#unable-to-add-to-path`).catch(() => { });
                }
            }, this.pathTroubleshootingOption);
        }
    }
}
exports.CommandExecutor = CommandExecutor;
//# sourceMappingURL=CommandExecutor.js.map